// src/pages/SearchPage.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import ArticleCard from '../components/ArticleCard';

const SearchPage = () => {
  const [results, setResults] = useState({ articles: [], calculators: [] });
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q');

  useEffect(() => {
    const performSearch = async () => {
      if (!query) return;
      
      setLoading(true);
      try {
        const [articlesRes, calculatorsRes] = await Promise.all([
          apiService.getArticles(),
          apiService.getCalculators()
        ]);

        const searchTerm = query.toLowerCase();
        const filteredArticles = articlesRes.data?.filter(article => 
          article.title.toLowerCase().includes(searchTerm) ||
          article.meta_description.toLowerCase().includes(searchTerm)
        ) || [];

        const filteredCalculators = calculatorsRes.data?.filter(calc =>
          calc.name.toLowerCase().includes(searchTerm) ||
          calc.description.toLowerCase().includes(searchTerm)
        ) || [];

        setResults({
          articles: filteredArticles,
          calculators: filteredCalculators
        });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-cyan-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Searching...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-cyan-50 py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">
          Search Results for "{query}"
        </h1>
        <p className="text-slate-600 mb-8">
          Found {results.articles.length} articles and {results.calculators.length} calculators
        </p>

        {/* Articles Results */}
        {results.articles.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.articles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>
        )}

        {/* Calculators Results */}
        {results.calculators.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Calculators</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.calculators.map(calculator => (
                <div key={calculator.id} className="card text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl font-bold">∑</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">
                    {calculator.name}
                  </h3>
                  <p className="text-slate-600 mb-4">{calculator.description}</p>
                  <a 
                    href={`/calculators/${calculator.slug}`}
                    className="inline-flex items-center text-orange-500 font-semibold"
                  >
                    Use Calculator
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* No Results */}
        {results.articles.length === 0 && results.calculators.length === 0 && query && (
          <div className="text-center py-12">
            <div className="text-slate-400 text-6xl mb-4">🔍</div>
            <p className="text-slate-600 text-lg">No results found for "{query}"</p>
            <p className="text-slate-500">Try different keywords or browse our categories</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;